import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

export function AdminCitiesPage() {
  const navigate = useNavigate();
  // Lista TODAS as cidades (ativas e inativas) via admin — o endpoint público oculta desativadas
  const [cities, setCities] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({ name: '', state: '', slug: '', lat: '', lng: '', population: '' });

  const fetchCities = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/cities');
      setCities(data.data || []);
    } catch {
      setCities([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { fetchCities(); }, [fetchCities]);

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      await api.post('/admin/cities', {
        name: form.name,
        state: form.state.toUpperCase(),
        slug: form.slug,
        lat: Number(form.lat),
        lng: Number(form.lng),
        population: form.population ? Number(form.population) : undefined,
      });
      setMessage({ type: 'ok', text: `Cidade "${form.name}" cadastrada!` });
      setForm({ name: '', state: '', slug: '', lat: '', lng: '', population: '' });
      fetchCities();
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao cadastrar cidade.' });
    } finally {
      setLoading(false);
    }
  };

  const toggleCity = async (city) => {
    setMessage(null);
    try {
      await api.patch(`/admin/cities/${city.id}`, { is_active: !city.is_active });
      setMessage({ type: 'ok', text: `${city.name} ${city.is_active ? 'desativada' : 'ativada'}.` });
      fetchCities();
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao atualizar cidade.' });
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, marginBottom: 12, outline: 'none', background: 'var(--nc-surface)', color: 'var(--nc-text)', boxSizing: 'border-box'
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nc-text-secondary)', marginBottom: 6 };

  return (
    <div className="nc-page">
      <div className="nc-pagehead">
        <div className="nc-row">
          <button onClick={() => navigate('/admin')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Cidades</h1>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {message && (
          <div role="status" style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${message.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
            color: message.type === 'ok' ? '#15803d' : '#b91c1c'
          }}>
            {message.text}
          </div>
        )}

        {/* Formulário */}
        <form onSubmit={handleCreate} style={{ background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)', padding: 18, marginBottom: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 12 }}>Cadastrar nova cidade</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Nome</label>
              <input type="text" value={form.name} onChange={set('name')} required placeholder="Ex.: Sumé" style={inputStyle} aria-label="Nome da cidade" />
            </div>
            <div>
              <label style={labelStyle}>UF</label>
              <input type="text" value={form.state} onChange={set('state')} required maxLength={2} placeholder="PB" style={inputStyle} aria-label="UF" />
            </div>
          </div>
          <label style={labelStyle}>Slug (identificador)</label>
          <input type="text" value={form.slug} onChange={set('slug')} required placeholder="ex.: sume-pb" style={inputStyle} aria-label="Slug" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Latitude</label>
              <input type="number" step="0.0001" value={form.lat} onChange={set('lat')} required style={inputStyle} aria-label="Latitude" />
            </div>
            <div>
              <label style={labelStyle}>Longitude</label>
              <input type="number" step="0.0001" value={form.lng} onChange={set('lng')} required style={inputStyle} aria-label="Longitude" />
            </div>
          </div>
          <label style={labelStyle}>População (opcional)</label>
          <input type="number" min="0" value={form.population} onChange={set('population')} style={inputStyle} aria-label="População" />
          <button type="submit" disabled={loading} className="nc-btn-primary" style={{ width: '100%' }}>
            {loading ? 'Cadastrando...' : 'Cadastrar cidade'}
          </button>
        </form>

        {/* Lista */}
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
          Cidades ({cities.length})
        </div>
        {loadingList ? <Loading /> : cities.length === 0 ? (
          <EmptyState icon="🏙️" title="Nenhuma cidade" description="Cadastre a primeira cidade do piloto." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {cities.map(c => (
              <div key={c.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: 12,
                background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)'
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: 'var(--nc-success-soft)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                }}>🏙️</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>
                    {c.name}/{c.state}
                    {!c.is_active && <span style={{ fontSize: 10, color: 'var(--nc-danger-text)', marginLeft: 8 }}>DESATIVADA</span>}
                  </div>
                  <div className="nc-caption">{c.slug} • {c.population ? `${c.population.toLocaleString('pt-BR')} hab.` : 'pop. não informada'}</div>
                </div>
                <button
                  onClick={() => toggleCity(c)}
                  style={{
                    padding: '7px 12px', borderRadius: 8, border: '1px solid var(--nc-border)',
                    background: 'var(--nc-surface)', color: c.is_active ? '#b91c1c' : '#0d5c3b', fontSize: 12, fontWeight: 500, cursor: 'pointer'
                  }}
                >
                  {c.is_active ? 'Desativar' : 'Ativar'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
